import { Box, Grid } from '@amzn/awsui-components-core';
import React from 'react';
import { useFinTechContext } from 'src/components/context/FintechContextProvider';
import { LoadingSpinner } from 'src/components/generic-components/LoadingSpinner';
import { EmptyStateMessage } from '../EmptyStateMessage';
import { ErrorContainer } from '../FinTechOpsCommonElements';
import { FinTechOpsCategories, FinTechOpsMessages, GRAPH_WIDTHS_BREAK_POINTS } from '../FinTechOpsConstants';
import { BarLineChartWithProps } from '../fintech-ops-charts/BarChartWithProps';
import { i18nStringHour } from '../i18nStrings';
import { useUserMetricsContext } from './UserMetricsContext';
import { AISummaryBox } from 'src/components/generic-components/ai-summary-components/AISummaryBox';
import { aggregateUserMetricsSummaryData } from './UserMetricsAggregator';
import { Titles } from 'src/components/das-finsuite/das-finsuite-constants';

export const UserMetricsOverview: React.FC = () => {
  const finOpsContext = useFinTechContext();
  const userMetricsContext = useUserMetricsContext();

  return (
    <div>
      {(() => {
        if (finOpsContext.finTechOpsUserMetricsDataStatus == 'loading') {
          return (
            <>
              <LoadingSpinner />
            </>
          );
        } else if (finOpsContext.finTechOpsUserMetricsDataStatus == 'error') {
          return (
            <>
              <ErrorContainer />
            </>
          );
        } else if (finOpsContext.finTechOpsUserMetricsDataStatus == 'pending') {
          return (
            <>
              <LoadingSpinner />
            </>
          );
        } else if (finOpsContext.finTechOpsUserMetricsDataStatus == 'finished') {
          const computeAggregatedData = () => {
            const data = aggregateUserMetricsSummaryData(userMetricsContext);
            return JSON.stringify(data);
          };

          return (
            <>
              <Box margin={{ bottom: 'l' }}>
                <AISummaryBox
                  pageType={FinTechOpsCategories.userMetrics}
                  title={`${Titles.TITLE_AI_SUMMARY}`}
                  statusKey={finOpsContext.finTechOpsUserMetricsDataStatus}
                  statusValue="finished"
                  filters={userMetricsContext.summaryFilters}
                  aggregatedData={computeAggregatedData()}
                  originalOptions={userMetricsContext.originalFilterOptions}
                />
              </Box>

              <Grid
                gridDefinition={[
                  { colspan: GRAPH_WIDTHS_BREAK_POINTS },
                  { colspan: GRAPH_WIDTHS_BREAK_POINTS },
                  { colspan: GRAPH_WIDTHS_BREAK_POINTS },
                  { colspan: GRAPH_WIDTHS_BREAK_POINTS }
                ]}
              >
                <BarLineChartWithProps
                  series={userMetricsContext.appViewershipByTeamData}
                  domainType="string"
                  xDomain={userMetricsContext.appViewershipByTeamXDomain}
                  yDomain={userMetricsContext.appViewershipByTeamYDomain}
                  xScaleType="categorical"
                  arialLabel="Total Application Views"
                  errorText={FinTechOpsMessages.errorText}
                  stackedBars={true}
                  hideFilter={true}
                  i18nStrings={i18nStringHour('Resource Type')}
                  emptyMessage={
                    <EmptyStateMessage title={FinTechOpsMessages.emptyStateMessage} subtitle={FinTechOpsMessages.emptyStateMessageDescription} />
                  }
                  xTitle="Months"
                  yTitle="Views"
                  noMatchMessage={FinTechOpsMessages.noMatchMessage}
                  headerName="Total Sub Application Views by Team"
                  showSummaryButton={true}
                  filters={userMetricsContext.summaryFilters}
                  masterFilters={userMetricsContext.originalFilterOptions}
                  pageType={'User Metrics'}
                />
                <BarLineChartWithProps
                  series={userMetricsContext.top5AppsViewership}
                  domainType="string"
                  xDomain={userMetricsContext.top5AppsViewershipXDomain}
                  yDomain={userMetricsContext.top5AppsViewershipYDomain}
                  xScaleType="categorical"
                  arialLabel="Top 5 Applications"
                  errorText={FinTechOpsMessages.errorText}
                  stackedBars={true}
                  hideFilter={true}
                  i18nStrings={i18nStringHour('Resource Type')}
                  emptyMessage={
                    <EmptyStateMessage title={FinTechOpsMessages.emptyStateMessage} subtitle={FinTechOpsMessages.emptyStateMessageDescription} />
                  }
                  xTitle="Months"
                  yTitle="Users"
                  noMatchMessage={FinTechOpsMessages.noMatchMessage}
                  headerName="Top 5 Sub Application - Unique Users"
                  showSummaryButton={true}
                  filters={userMetricsContext.summaryFilters}
                  masterFilters={userMetricsContext.originalFilterOptions}
                  pageType={'User Metrics'}
                />
                <BarLineChartWithProps
                  series={userMetricsContext.uniqueUsersByApplication}
                  domainType="string"
                  xDomain={userMetricsContext.uniqueUsersByApplicationXDomain}
                  yDomain={userMetricsContext.uniqueUsersByApplicationYDomain}
                  xScaleType="categorical"
                  arialLabel="Total Unique Users by Application"
                  errorText={FinTechOpsMessages.errorText}
                  stackedBars={true}
                  hideFilter={true}
                  i18nStrings={i18nStringHour('Resource Type')}
                  emptyMessage={
                    <EmptyStateMessage title={FinTechOpsMessages.emptyStateMessage} subtitle={FinTechOpsMessages.emptyStateMessageDescription} />
                  }
                  xTitle="Months"
                  yTitle="Users"
                  noMatchMessage={FinTechOpsMessages.noMatchMessage}
                  headerName="Total Unique Users by Application"
                  showSummaryButton={true}
                  filters={userMetricsContext.summaryFilters}
                  masterFilters={userMetricsContext.originalFilterOptions}
                  pageType={'User Metrics'}
                />
                <BarLineChartWithProps
                  series={userMetricsContext.totalApplicationViews}
                  domainType="string"
                  xDomain={userMetricsContext.totalApplicationViewsXDomain}
                  yDomain={userMetricsContext.totalApplicationViewsYDomain}
                  xScaleType="categorical"
                  arialLabel="Total Application Views"
                  errorText={FinTechOpsMessages.errorText}
                  stackedBars={true}
                  hideFilter={true}
                  i18nStrings={i18nStringHour('Resource Type')}
                  emptyMessage={
                    <EmptyStateMessage title={FinTechOpsMessages.emptyStateMessage} subtitle={FinTechOpsMessages.emptyStateMessageDescription} />
                  }
                  xTitle="Months"
                  yTitle="Views"
                  noMatchMessage={FinTechOpsMessages.noMatchMessage}
                  headerName="Total Views by Application"
                  showSummaryButton={true}
                  filters={userMetricsContext.summaryFilters}
                  masterFilters={userMetricsContext.originalFilterOptions}
                  pageType={'User Metrics'}
                />
              </Grid>
            </>
          );
        }
      })()}
    </div>
  );
};
